import { ADMIN_IDLE_TIMEOUT_MS } from '../constants/bookingConst';

// A till's sign-in, remembered across page loads — one record PER CLIENT.
//
// Keyed by client id rather than one record for the browser, because the two
// facts are different: being signed in at club-a says nothing about club-b,
// and a single shared record was how a session could once be carried from one
// client's till to another's by editing the URL.
//
// WHAT THIS RECORD IS, EXACTLY. An email address and two timestamps. It is NOT
// proof of a password: the till verifies credentials on a throwaway auth
// instance (see verifyCredentials) and that verification is not storable. So
// this is a convenience token, and restoring from it re-checks the client's
// staff list before trusting it — an address removed from that list cannot
// come back through a stale record. Anyone who can edit this browser's storage
// could extend their own session, which is the same person who already has
// the counter in front of them; the real boundary is the staff list and the
// database rules, not this.

const KEY_PREFIX = 'pos-till-session:';

/** The storage key for one client. @param {string} clientId @returns {string} */
const keyFor = (clientId) => `${KEY_PREFIX}${String(clientId || '').trim()}`;

/**
 * The two places a record can live. localStorage outlasts the tab — that is
 * "Keep me signed in". sessionStorage survives a refresh but ends with the
 * tab, which is what an unticked box asks for on a shared counter machine.
 *
 * Read lazily and wrapped, because touching either property throws in some
 * private-browsing modes.
 *
 * @returns {Storage[]} the stores that are usable, persistent one first
 */
const usableStores = () => {
  const stores = [];
  try { if (window.localStorage) stores.push(window.localStorage); } catch { /* unavailable */ }
  try { if (window.sessionStorage) stores.push(window.sessionStorage); } catch { /* unavailable */ }
  return stores;
};

/**
 * Reads the raw record and the store it came from, or null.
 *
 * Every storage access here is wrapped: private browsing, disabled storage
 * and a corrupted value must all end in "not signed in" rather than a crashed
 * till.
 *
 * @param {string} clientId
 * @returns {?{record: {email: string, startedAtMs: number, lastActiveMs: number}, store: Storage}}
 */
const readEntry = (clientId) => {
  for (const store of usableStores()) {
    try {
      const raw = store.getItem(keyFor(clientId));
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed?.email) return { record: parsed, store };
    } catch {
      // A corrupted value in one store does not hide a good one in the other.
    }
  }
  return null;
};

/**
 * Reads the raw record, or null. See readEntry.
 *
 * @param {string} clientId
 * @returns {?{email: string, startedAtMs: number, lastActiveMs: number}}
 */
const readRecord = (clientId) => readEntry(clientId)?.record || null;

/**
 * Starts a session for this client, replacing any previous one.
 *
 * @param {string} clientId
 * @param {string} email - the address the till verified.
 * @param {boolean} [remember=true] - "Keep me signed in": true keeps the
 *   session after the tab closes; false ends it with the tab (a refresh still
 *   keeps it).
 * @returns {void}
 */
export const startTillSession = (clientId, email, remember = true) => {
  const now = Date.now();
  // Cleared first, so a record left in the OTHER store by an earlier sign-in
  // cannot outlive the choice made this time.
  clearTillSession(clientId);
  try {
    const store = remember ? window.localStorage : window.sessionStorage;
    store.setItem(keyFor(clientId), JSON.stringify({
      email: String(email || '').trim(),
      startedAtMs: now,
      lastActiveMs: now,
    }));
  } catch {
    // Storage unavailable. The till still works for this page load; it just
    // asks again after a refresh, which is the old behaviour.
  }
};

/**
 * Marks the till as in use, so the idle window starts again from now.
 *
 * Cheap enough to call on every click: one read, one write, no parsing of
 * anything but this key.
 *
 * @param {string} clientId
 * @returns {void}
 */
export const touchTillSession = (clientId) => {
  const entry = readEntry(clientId);
  if (!entry) return;
  try {
    // Written back to the store it came from, so a tab-only session stays
    // tab-only however long the shift runs.
    entry.store.setItem(keyFor(clientId), JSON.stringify({ ...entry.record, lastActiveMs: Date.now() }));
  } catch {
    // See startTillSession.
  }
};

/** Ends the session for this client, in both stores. @param {string} clientId @returns {void} */
export function clearTillSession(clientId) {
  usableStores().forEach((store) => {
    try {
      store.removeItem(keyFor(clientId));
    } catch {
      // Nothing to do — a record that cannot be removed also could not be read.
    }
  });
}

/**
 * How long this session has been idle, in milliseconds.
 *
 * @param {string} clientId
 * @returns {?number} null when there is no session.
 */
export const tillSessionIdleMs = (clientId) => {
  const record = readRecord(clientId);
  if (!record) return null;
  return Date.now() - Number(record.lastActiveMs || 0);
};

/**
 * The session to restore for this client, or null.
 *
 * With the idle timeout ON, a session idle for longer than
 * ADMIN_IDLE_TIMEOUT_MS is expired HERE — removed, not merely hidden, so the
 * next read cannot resurrect it. With the timeout OFF, a session lasts until
 * someone signs out: that is the whole point of the setting, and a till on a
 * dedicated machine behind the counter is exactly the case it exists for.
 *
 * @param {string} clientId
 * @param {boolean} idleTimeoutEnabled - Admin -> Config.
 * @returns {?{email: string, startedAtMs: number, lastActiveMs: number}}
 */
export const getTillSession = (clientId, idleTimeoutEnabled) => {
  const record = readRecord(clientId);
  if (!record) return null;

  if (idleTimeoutEnabled) {
    const idleFor = Date.now() - Number(record.lastActiveMs || 0);
    // A record with a missing or future timestamp counts as expired rather
    // than as infinitely fresh — the safe direction for a clock that has been
    // tampered with or a record written by an older build.
    if (!Number.isFinite(idleFor) || idleFor < 0 || idleFor > ADMIN_IDLE_TIMEOUT_MS) {
      clearTillSession(clientId);
      return null;
    }
  }

  return record;
};

export { ADMIN_IDLE_TIMEOUT_MS };
